import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import ClockLoader from "react-spinners/ClockLoader";

import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { Box, Grid, Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";

import * as PracticeActions from "../../state/redux/practiceSession";
import { deepCopyObject } from "../../utils/helpers";
import PracticeDialog from "../dialogs/practiceDialog";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const groupLikeDays = businessHours => {
  let days = [
    { order: 0, day: "monday" },
    { order: 1, day: "tuesday" },
    { order: 2, day: "wednesday" },
    { order: 3, day: "thursday" },
    { order: 4, day: "friday" },
    { order: 5, day: "saturday" },
    { order: 6, day: "sunday" },
  ].sort((a, b) => a.order - b.order);

  let schedule = days.reduce((adjustedDays, item, index) => {
    let open = `${item.day}Open`,
      close = `${item.day}Close`,
      thisItem = { days: item.day, open: businessHours[open], close: businessHours[close] };
    if (
      businessHours[thisItem.days] &&
      adjustedDays.some(
        thisDay => format(new Date(thisDay.open), "h:m") === format(new Date(thisItem.open), "h:m"),
      ) &&
      adjustedDays.some(
        thisDay =>
          format(new Date(thisDay.close), "h:m") === format(new Date(thisItem.close), "h:m"),
      )
    ) {
      let myIndex = adjustedDays
        .slice()
        .reverse()
        .findIndex(
          thisDay =>
            format(new Date(thisDay.open), "h:m") === format(new Date(thisItem.open), "h:m") &&
            format(new Date(thisDay.close), "h:m") === format(new Date(thisItem.close), "h:m"),
        );
      if (
        adjustedDays
          .slice()
          .reverse()
          [myIndex].days.search(days[index - 1].day) !== -1 &&
        myIndex === 0
      ) {
        thisItem.days = adjustedDays.slice().reverse()[myIndex].days.concat(` - ${item.day}`);
        adjustedDays.reverse().splice(myIndex, 1, thisItem);
        adjustedDays.reverse();
      } else {
        adjustedDays.push(thisItem);
      }
    } else {
      adjustedDays.push(thisItem);
    }

    return adjustedDays;
  }, []);

  schedule = schedule.reduce((scheduleDays, item, index) => {
    let myDays = item.days.replace(/-(.*)-/, "-");

    myDays = myDays.split(" ");
    if (myDays.length > 2) {
      myDays.forEach((string, index) => (myDays[index] = string.substring(0, 3)));
    }

    scheduleDays.splice(index, 1, { ...item, days: myDays.join(" ") });

    return scheduleDays;
  }, []);

  return schedule;
};

const PracticeDetails = props => {
  const { practiceODM } = props;

  const theme = useTheme();

  const [selectedPractice, setSelectedPractice] = useState(null);

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (practiceODM != null) {
      setTitleText(practiceODM.omniData.name);
    } else setTitleText("");
  }, [practiceODM]);

  return (
    <Widget>
      <WidgetTitle title="Practice" text={titleText} loading={practiceODM == null}>
        <OmniResponsiveButton
          tooltipTitle="Edit this practice details"
          buttonText="Edit"
          icon={<EditIcon />}
          onClick={() => {
            setSelectedPractice(practiceODM);
          }}
        />
      </WidgetTitle>

      {practiceODM == null && <WidgetSkeleton />}
      {practiceODM != null && (
        <WidgetList>
          <Grid container spacing={1}>
            {/* Contact */}
            <Grid
              container
              item
              alignItems="flex-start"
              sx={{
                marginTop: `-${theme.spacing(2)}`,
                webkitUserSelect: "none",
                userSelect: "none",
              }}
            >
              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  sx={{ marginBottom: `-${theme.spacing(1)}` }}
                >{`Contact`}</Typography>
              </Grid>
              <Tooltip title={practiceODM.omniData.phone} placement="left">
                <IconButton
                  sx={{
                    marginTop: `-${theme.spacing(1)}`,
                    marginBottom: `-${theme.spacing(1)}`,
                    color: theme.palette.secondary.main,
                  }}
                  href={`tel:${practiceODM.omniData.phone}`}
                  size="large"
                >
                  <PhoneIcon />
                </IconButton>
              </Tooltip>
              <Grid item xs={9}>
                <Typography variant="h6">{practiceODM.omniData.phone}</Typography>
              </Grid>
            </Grid>

            <Grid container item alignItems="flex-start">
              <Tooltip title={practiceODM.omniData.email} placement="left">
                <IconButton
                  sx={{
                    marginTop: `-${theme.spacing(1)}`,
                    marginBottom: `-${theme.spacing(1)}`,
                    color: theme.palette.secondary.main,
                  }}
                  href={`mailto:${practiceODM.omniData.email}`}
                  size="large"
                >
                  <EmailIcon />
                </IconButton>
              </Tooltip>
              <Grid item xs={9}>
                <Typography variant="h6">{practiceODM.omniData.email}</Typography>
              </Grid>
              <Divider
                sx={{
                  borderRadius: "4px",
                  borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
                  height: "1px",
                  width: "100%",
                  marginBottom: theme.spacing(0.5),
                  marginTop: 0,
                }}
                orientation="vertical"
              />
            </Grid>

            {/* Address */}
            <Grid container item alignItems="flex-start" sx={{ marginTop: `-${theme.spacing(1)}` }}>
              <Grid item xs={12}>
                <Typography variant="overline">{`Address`}</Typography>
              </Grid>
              <Grid container item spacing={1}>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{
                      userSelect: "none",
                      webkitUserSelect: "none",
                      marginTop: `-${theme.spacing(1)}`,
                      marginLeft: theme.spacing(1),
                    }}
                  >
                    {practiceODM.omniData.address.streetAddress}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="h6"
                    sx={{
                      userSelect: "none",
                      webkitUserSelect: "none",
                      marginTop: `-${theme.spacing(1)}`,
                      marginLeft: theme.spacing(1),
                    }}
                  >
                    {`${practiceODM.omniData.address.locality} ${practiceODM.omniData.address.region}, ${practiceODM.omniData.address.postalCode}`}
                  </Typography>
                </Grid>
              </Grid>

              <Divider
                sx={{
                  borderRadius: "4px",
                  borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
                  height: "1px",
                  width: "100%",
                  marginBottom: theme.spacing(0.5),
                  marginTop: 0,
                }}
                orientation="vertical"
              />
            </Grid>

            {/* Business hours */}
            <Grid container item alignItems="flex-start" sx={{ marginTop: `-${theme.spacing(1)}` }}>
              <Grid item xs={12}>
                <Typography variant="overline">{`Business Hours`}</Typography>
              </Grid>
              <Grid container item alignItems="flex-start" spacing={1}>
                {groupLikeDays(practiceODM.omniData.businessHours).map(item => {
                  return (
                    item.open &&
                    item.close && (
                      <Grid
                        item
                        key={`${item.days}open`}
                        container
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Grid item>
                          <Typography
                            variant="body1"
                            sx={{
                              userSelect: "none",
                              webkitUserSelect: "none",
                              marginTop: `-${theme.spacing(1)}`,
                              marginLeft: theme.spacing(1),
                              textTransform: "capitalize",
                            }}
                          >
                            {`${item.days}`}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography
                            variant="body2"
                            sx={{
                              webkitUserSelect: "none",
                              userSelect: "none",
                              marginTop: `-${theme.spacing(0.75)}`,
                            }}
                          >
                            {`${format(new Date(item.open), "h:mm aa")} - ${format(
                              new Date(item.close),
                              "h:mm aa",
                            )}`}
                          </Typography>
                        </Grid>
                      </Grid>
                    )
                  );
                })}
              </Grid>
              <Divider
                sx={{
                  borderRadius: "4px",
                  borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
                  height: "1px",
                  width: "100%",
                  marginBottom: theme.spacing(0.5),
                  marginTop: 0,
                }}
                orientation="vertical"
              />
            </Grid>
          </Grid>
        </WidgetList>
      )}

      {!practiceODM && (
        <WidgetList>
          <Box>
            <ClockLoader color={theme.palette.background.contrastText} loading={true} size={100} />
          </Box>
        </WidgetList>
      )}

      {selectedPractice != null && (
        <PracticeDialog
          open={selectedPractice != null}
          dialogTitle={"Create Practice"}
          omniDMO={deepCopyObject(selectedPractice)}
          fullWidth={true}
          onDelete={selectedPractice => {
            props.deletePracticeRequest(selectedPractice._id);
            setSelectedPractice(null);
          }}
          onAccept={selectedPractice => {
            props.updatePracticeRequest(selectedPractice);
            setSelectedPractice(null);
          }}
          onClose={() => {
            setSelectedPractice(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  practiceODM: state.practiceSession.practiceODM,
});

const mapDispatchToProps = {
  updatePracticeRequest: PracticeActions.updatePracticeRequest,
  deletePracticeRequest: PracticeActions.deletePracticeRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PracticeDetails);
